import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Radio, Sparkles, Truck, User, Zap } from 'lucide-react';
import { TRANSPORT_VENDORS } from './mockTransportVendorData';
import {
  buildDriverPerformanceRoster,
  getDriverScorecard,
  runSmartMatchAlgorithm,
} from './dispatchOptimizerUtils';
import {
  buildLiveOrder,
  getNextOrderSequence,
  INITIAL_LIVE_ORDERS,
  pickRandomSimulationLocation,
  pickRandomSimulationVolume,
} from './mockLiveDispatchOrders';

const TERMINAL_ORDERS_KEY = 'terminal_orders';

function loadOrdersFromStorage() {
  try {
    const raw = localStorage.getItem(TERMINAL_ORDERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed.map((order) => ({ ...order }));
  } catch (e) {
    console.error('[LiveDispatchMonitor] localStorage read failed:', e);
    return null;
  }
}

function saveOrdersToStorage(orders) {
  try {
    localStorage.setItem(TERMINAL_ORDERS_KEY, JSON.stringify(orders));
  } catch (e) {
    console.error('[LiveDispatchMonitor] localStorage write failed:', e);
  }
}

function validateInjectionForm(location, volume) {
  const errors = { location: '', volume: '' };
  const trimmedLocation = String(location ?? '').trim();
  const numericVolume = Number(volume);

  if (!trimmedLocation) {
    errors.location = 'Client / location name cannot be blank.';
  }

  if (!Number.isFinite(numericVolume) || numericVolume <= 0) {
    errors.volume = 'Volume must be a positive number greater than zero.';
  }

  return errors;
}

const P = {
  silver: '#F1F5F9',
  white: '#FFFFFF',
  grid: '#E2E8F0',
  border: '#CBD5E1',
  blue: '#3B82F6',
  blueHover: '#2563EB',
  blueTint: '#EFF6FF',
  text: '#1E293B',
  muted: '#64748B',
  earn: '#059669',
  warn: '#D97706',
};

const btnLightBlue = {
  backgroundColor: '#BFDBFE',
  borderColor: '#93C5FD',
  borderWidth: 1,
  borderStyle: 'solid',
  color: '#000000',
};

const btnLightBlueHover = '#93C5FD';
const btnLightBlueDisabled = {
  backgroundColor: '#E2E8F0',
  borderColor: P.grid,
  borderWidth: 1,
  borderStyle: 'solid',
  color: '#64748B',
};

function applyBtnHover(e, enabled = true) {
  if (enabled) e.currentTarget.style.backgroundColor = btnLightBlueHover;
}

function applyBtnLeave(e, enabled = true) {
  if (enabled) e.currentTarget.style.backgroundColor = btnLightBlue.backgroundColor;
}


const lightCard = {
  backgroundColor: P.white,
  borderColor: P.grid,
};

const STATUS_STYLE = {
  Unassigned: { bg: '#FFFBEB', color: P.warn, ring: '#FDE68A' },
  Dispatched: { bg: P.blueTint, color: P.blue, ring: '#BFDBFE' },
  Delivered: { bg: '#ECFDF5', color: P.earn, ring: '#A7F3D0' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Unassigned;
  return (
    <span
      className="inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"
      style={{ backgroundColor: s.bg, color: s.color, boxShadow: `inset 0 0 0 1px ${s.ring}` }}
    >
      {status}
    </span>
  );
}

function DispatchToast({ visible, onDismiss }) {
  useEffect(() => {
    if (!visible) return undefined;
    const timer = setTimeout(onDismiss, 4200);
    return () => clearTimeout(timer);
  }, [visible, onDismiss]);

  if (!visible) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-6 right-6 z-50 max-w-sm animate-[fadeSlideIn_0.35s_ease-out]"
      role="status"
      aria-live="polite"
    >
      <div
        className="flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg"
        style={{
          backgroundColor: P.blueTint,
          borderColor: P.blue,
          borderWidth: 1,
          borderStyle: 'solid',
          color: P.text,
        }}
      >
        <Zap className="mt-0.5 h-5 w-5 shrink-0" style={{ color: P.blue }} />
        <div>
          <p className="text-sm font-extrabold" style={{ color: P.blue }}>
            Dispatch Confirmed
          </p>
          <p className="mt-0.5 text-xs font-semibold" style={{ color: P.muted }}>
            Trip Successfully Transmitted to Mobile App Pipeline
          </p>
        </div>
      </div>
    </div>
  );
}

const selectStyle = {
  backgroundColor: P.white,
  borderColor: P.grid,
  borderWidth: 1,
  borderStyle: 'solid',
  color: P.text,
  colorScheme: 'light',
};

const inputStyle = {
  ...selectStyle,
};

function DriverScorecard({ driverId, driverName, tripsCompleted }) {
  if (!driverId) return null;
  const score = getDriverScorecard(driverId);

  return (
    <aside
      className="w-full shrink-0 xl:w-72 xl:self-start"
      aria-label="Driver scorecard"
    >
      <div
        className="rounded-2xl border p-5 shadow-sm xl:sticky xl:top-4"
        style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
      >
      <div className="mb-4 flex items-center gap-3">
        <div
          className="flex h-11 w-11 items-center justify-center rounded-full"
          style={{ backgroundColor: P.blueTint, boxShadow: `inset 0 0 0 1px ${P.grid}` }}
        >
          <User className="h-5 w-5" style={{ color: P.blue }} strokeWidth={1.75} />
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: P.blue }}>
            Driver Scorecard
          </p>
          <p className="text-sm font-extrabold" style={{ color: P.text }}>
            {driverName}
          </p>
          <p className="font-mono text-[10px] font-bold" style={{ color: P.muted }}>
            {driverId} · {tripsCompleted} trips (Jun 2026)
          </p>
        </div>
      </div>
      <ul className="space-y-2">
        {[
          { label: 'Trip Accuracy', value: score.tripAccuracy },
          { label: 'Fuel Efficiency', value: score.fuelEfficiency },
          { label: 'Safety Record', value: score.safetyRecord },
        ].map((row) => (
          <li
            key={row.label}
            className="flex items-center justify-between rounded-lg border px-3 py-2.5"
            style={{ borderColor: P.grid, backgroundColor: P.silver }}
          >
            <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: P.muted }}>
              {row.label}
            </span>
            <span className="text-xs font-extrabold" style={{ color: P.text }}>
              {row.value}
            </span>
          </li>
        ))}
      </ul>
      </div>
    </aside>
  );
}

export default function LiveDispatchMonitor() {
  const [orders, setOrders] = useState(() => {
    const stored = loadOrdersFromStorage();
    if (stored) return stored;
    return INITIAL_LIVE_ORDERS.map((o) => ({ ...o }));
  });
  const [toastVisible, setToastVisible] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [volumeInput, setVolumeInput] = useState(50000);
  const [formErrors, setFormErrors] = useState({ location: '', volume: '' });
  const [analyzingOrderId, setAnalyzingOrderId] = useState(null);
  const [scorecardDriverId, setScorecardDriverId] = useState('');
  const sequenceRef = useRef(getNextOrderSequence(loadOrdersFromStorage() || INITIAL_LIVE_ORDERS));

  useEffect(() => {
    saveOrdersToStorage(orders);
  }, [orders]);

  const appendOrder = useCallback((order) => {
    setOrders((prev) => [order, ...prev]);
  }, []);

  const createOrderFromInputs = useCallback(
    (location, liters) => {
      const sequence = sequenceRef.current;
      sequenceRef.current += 1;
      return buildLiveOrder({ location, liters, sequence });
    },
    [],
  );

  const handleCreateOrder = (e) => {
    e.preventDefault();
    const location = locationInput.trim();
    const errors = validateInjectionForm(location, volumeInput);

    if (errors.location || errors.volume) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({ location: '', volume: '' });
    appendOrder(createOrderFromInputs(location, Number(volumeInput)));
    setLocationInput('');
    setVolumeInput(50000);
  };

  const handleSimulateLiveOrder = () => {
    const location = pickRandomSimulationLocation();
    const liters = pickRandomSimulationVolume();

    appendOrder(createOrderFromInputs(location, liters));
  };

  const activeVendors = useMemo(
    () => TRANSPORT_VENDORS.filter((v) => v.contractStatus === 'Active'),
    [],
  );

  const driverPerformanceRoster = useMemo(() => buildDriverPerformanceRoster(), []);

  const driverRoster = useMemo(
    () =>
      driverPerformanceRoster.map((d) => ({
        id: d.id,
        name: d.name,
        tripsCompleted: d.tripsCompleted,
      })),
    [driverPerformanceRoster],
  );

  const scorecardDriver = useMemo(
    () => driverRoster.find((d) => d.id === scorecardDriverId) || null,
    [driverRoster, scorecardDriverId],
  );

  const optimizerPreview = useMemo(
    () => runSmartMatchAlgorithm(activeVendors, driverPerformanceRoster),
    [activeVendors, driverPerformanceRoster],
  );

  const stats = useMemo(() => {
    const unassigned = orders.filter((o) => o.status === 'Unassigned').length;
    const dispatched = orders.filter((o) => o.status === 'Dispatched').length;
    const delivered = orders.filter((o) => o.status === 'Delivered').length;
    return { unassigned, dispatched, delivered, total: orders.length };
  }, [orders]);

  const updateOrder = useCallback((orderId, patch) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, ...patch } : o)),
    );
  }, []);

  const handleDriverSelect = (orderId, driverId) => {
    updateOrder(orderId, { driverId });
    if (driverId) setScorecardDriverId(driverId);
  };

  const handleSmartSuggest = (orderId) => {
    if (analyzingOrderId) return;

    setAnalyzingOrderId(orderId);

    setTimeout(() => {
      const match = runSmartMatchAlgorithm(activeVendors, driverPerformanceRoster);
      updateOrder(orderId, {
        vendorId: match.vendorId,
        driverId: match.driverId,
      });
      if (match.driverId) setScorecardDriverId(match.driverId);
      setAnalyzingOrderId(null);
    }, 1000);
  };

  const handleDispatch = (orderId) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order || order.status !== 'Unassigned') return;
    if (!order.vendorId || !order.driverId) return;

    updateOrder(orderId, { status: 'Dispatched' });
    setToastVisible(true);
  };

  const dismissToast = useCallback(() => setToastVisible(false), []);

  return (
    <div className="flex min-h-[calc(100dvh-12rem)] w-full flex-col font-sans">
      <header className="mb-5 shrink-0">
        <span
          className="inline-block rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest"
          style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid', color: P.blue }}
        >
          Mission Control · Live Assignment
        </span>
        <h1 className="mt-2 text-2xl font-black tracking-tight sm:text-3xl" style={{ color: P.text }}>
          Live Dispatch Monitor
        </h1>
        <p className="mt-1 text-xs font-bold uppercase tracking-wider" style={{ color: P.muted }}>
          Smart optimizer · Vendor &amp; driver matching · Mobile pipeline sync
        </p>
      </header>

      <section
        className="mb-5 shrink-0 rounded-2xl border p-4 shadow-sm sm:p-5"
        style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid', borderColor: P.blue, boxShadow: `0 0 0 1px ${P.blueTint}` }}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full"
              style={{ backgroundColor: P.blueTint, boxShadow: `inset 0 0 0 2px ${P.white}` }}
            >
              <Sparkles className="h-6 w-6" style={{ color: P.blue }} strokeWidth={1.75} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: P.blue }}>
                Smart Optimizer Dashboard
              </p>
              <p className="text-sm font-extrabold" style={{ color: P.text }}>
                AI-assisted vendor &amp; driver pairing engine
              </p>
              <p className="mt-0.5 text-xs font-medium" style={{ color: P.muted }}>
                Scans fleet availability + payroll trip counts per order
              </p>
            </div>
          </div>
          <div
            className="rounded-xl border px-4 py-3 text-xs"
            style={{ backgroundColor: P.silver, borderColor: P.grid }}
          >
            <p className="font-bold uppercase tracking-wide" style={{ color: P.muted }}>
              Current Best Match Preview
            </p>
            <p className="mt-1 font-semibold" style={{ color: P.text }}>
              {optimizerPreview.vendorName || '—'}{' '}
              <span style={{ color: P.muted }}>({optimizerPreview.vendorFleets} tankers)</span>
            </p>
            <p className="font-semibold" style={{ color: P.text }}>
              {optimizerPreview.driverName || '—'}{' '}
              <span style={{ color: P.blue }}>{optimizerPreview.driverTrips} trips</span>
            </p>
          </div>
        </div>
      </section>

      <section
        className="mb-5 shrink-0 rounded-2xl border p-4 shadow-sm sm:p-5"
        style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
      >
        <p
          className="mb-3 text-[10px] font-bold uppercase tracking-widest"
          style={{ color: P.muted }}
        >
          Quick Order Injection Form
        </p>
        <form
          onSubmit={handleCreateOrder}
          className="flex flex-col gap-3 lg:flex-row lg:items-end"
        >
          <div className="min-w-0 flex-1">
            <label
              htmlFor="dispatch-location"
              className="mb-1 block text-[10px] font-bold uppercase tracking-wide"
              style={{ color: P.muted }}
            >
              Location / Client Name
            </label>
            <input
              id="dispatch-location"
              type="text"
              value={locationInput}
              onChange={(e) => {
                setLocationInput(e.target.value);
                if (formErrors.location) {
                  setFormErrors((prev) => ({ ...prev, location: '' }));
                }
              }}
              placeholder="e.g. Ajman Terminal"
              className="w-full rounded-lg px-3 py-2.5 text-sm font-semibold outline-none focus:ring-2"
              style={{
                ...inputStyle,
                borderColor: formErrors.location ? '#FCA5A5' : P.grid,
              }}
              aria-invalid={Boolean(formErrors.location)}
              aria-describedby={formErrors.location ? 'dispatch-location-error' : undefined}
            />
            {formErrors.location ? (
              <p
                id="dispatch-location-error"
                className="mt-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold"
                style={{
                  backgroundColor: P.silver,
                  borderColor: P.grid,
                  color: '#DC2626',
                }}
                role="alert"
              >
                {formErrors.location}
              </p>
            ) : null}
          </div>
          <div className="w-full lg:w-48">
            <label
              htmlFor="dispatch-volume"
              className="mb-1 block text-[10px] font-bold uppercase tracking-wide"
              style={{ color: P.muted }}
            >
              Volume (Liters)
            </label>
            <input
              id="dispatch-volume"
              type="number"
              min={1}
              step={1000}
              value={volumeInput}
              onChange={(e) => {
                setVolumeInput(e.target.value === '' ? '' : Number(e.target.value));
                if (formErrors.volume) {
                  setFormErrors((prev) => ({ ...prev, volume: '' }));
                }
              }}
              className="w-full rounded-lg px-3 py-2.5 font-mono text-sm font-bold outline-none focus:ring-2"
              style={{
                ...inputStyle,
                borderColor: formErrors.volume ? '#FCA5A5' : P.grid,
              }}
              aria-invalid={Boolean(formErrors.volume)}
              aria-describedby={formErrors.volume ? 'dispatch-volume-error' : undefined}
            />
            {formErrors.volume ? (
              <p
                id="dispatch-volume-error"
                className="mt-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold"
                style={{
                  backgroundColor: P.silver,
                  borderColor: P.grid,
                  color: '#DC2626',
                }}
                role="alert"
              >
                {formErrors.volume}
              </p>
            ) : null}
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-black uppercase tracking-wide shadow-sm transition"
            style={btnLightBlue}
            onMouseEnter={(e) => applyBtnHover(e)}
            onMouseLeave={(e) => applyBtnLeave(e)}
          >
            <Plus className="h-4 w-4" strokeWidth={2.5} />
            + Create Order
          </button>
          <button
            type="button"
            onClick={handleSimulateLiveOrder}
            className="inline-flex items-center gap-3 rounded-full px-4 py-2 text-left shadow-sm transition lg:mb-0"
            style={btnLightBlue}
            onMouseEnter={(e) => applyBtnHover(e)}
            onMouseLeave={(e) => applyBtnLeave(e)}
          >
            <span
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-lg"
              style={{ backgroundColor: P.white, boxShadow: `inset 0 0 0 1px ${P.grid}` }}
            >
              🤖
            </span>
            <span className="text-[10px] font-black uppercase leading-tight tracking-wide">
              Simulate Live
              <br />
              Customer Order
            </span>
          </button>
        </form>
      </section>

      <div className="mb-5 grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Active Orders', value: stats.total, accent: P.text },
          { label: 'Unassigned', value: stats.unassigned, accent: P.warn },
          { label: 'Dispatched', value: stats.dispatched, accent: P.blue },
          { label: 'Delivered', value: stats.delivered, accent: P.earn },
        ].map((chip) => (
          <div
            key={chip.label}
            className="rounded-xl border px-4 py-3 text-center shadow-sm"
            style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
          >
            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: P.muted }}>
              {chip.label}
            </p>
            <p className="mt-1 font-mono text-2xl font-black tabular-nums" style={{ color: chip.accent }}>
              {chip.value}
            </p>
          </div>
        ))}
      </div>

      <div
        className={`flex min-h-0 flex-1 flex-col gap-5 ${scorecardDriver ? 'xl:flex-row xl:items-stretch' : ''}`}
      >
        <section
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border shadow-sm"
          style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
        >
          <div
            className="flex shrink-0 items-center gap-2 border-b px-4 py-3 sm:px-5 sm:py-4"
            style={{
              backgroundColor: P.silver,
              borderColor: P.grid,
              borderBottomWidth: 1,
              borderBottomStyle: 'solid',
            }}
          >
            <Radio className="h-5 w-5 shrink-0" style={{ color: P.blue }} strokeWidth={1.75} />
            <div className="min-w-0">
              <h2 className="text-sm font-extrabold" style={{ color: P.text }}>
                Active Fuel Delivery Orders
              </h2>
              <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: P.muted }}>
                Select vendor + driver · Confirm dispatch to mobile app
              </p>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full table-fixed text-left text-sm">
              <thead className="sticky top-0 z-10">
                <tr
                  className="text-[10px] font-bold uppercase tracking-wider"
                  style={{ color: P.muted, backgroundColor: P.white }}
                >
                  <th className="w-[26%] border-b px-4 py-3 sm:px-5" style={{ borderColor: P.grid }}>
                    Order Reference
                  </th>
                  <th className="w-[14%] border-b px-4 py-3 sm:px-5" style={{ borderColor: P.grid }}>
                    Volume / Cargo
                  </th>
                  <th className="w-[10%] border-b px-4 py-3 sm:px-5" style={{ borderColor: P.grid }}>
                    Status
                  </th>
                  <th className="w-[18%] border-b px-4 py-3 sm:px-5" style={{ borderColor: P.grid }}>
                    Transport Vendor
                  </th>
                  <th className="w-[18%] border-b px-4 py-3 sm:px-5" style={{ borderColor: P.grid }}>
                    Assigned Driver
                  </th>
                  <th className="w-[14%] border-b px-4 py-3 text-center sm:px-5" style={{ borderColor: P.grid }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const isUnassigned = order.status === 'Unassigned';
                  const canDispatch = isUnassigned && order.vendorId && order.driverId;
                  const vendorName = activeVendors.find((v) => v.vendorId === order.vendorId)?.companyName;
                  const driverName = driverRoster.find((d) => d.id === order.driverId)?.name;

                  return (
                    <tr
                      key={order.id}
                      style={{ borderTop: `1px solid ${P.grid}`, backgroundColor: P.white }}
                    >
                      <td className="px-4 py-3 align-top sm:px-5 sm:py-4">
                        <p className="break-words font-semibold leading-snug" style={{ color: P.text }}>
                          {order.orderRef}
                        </p>
                        <p className="mt-0.5 truncate font-mono text-[10px] font-bold" style={{ color: P.blue }}>
                          {order.id}
                        </p>
                      </td>
                      <td className="px-4 py-3 align-top text-xs font-medium sm:px-5 sm:py-4" style={{ color: P.muted }}>
                        {order.volume}
                      </td>
                      <td className="px-4 py-3 align-top sm:px-5 sm:py-4">
                        <StatusBadge status={order.status} />
                      </td>
                      <td className="px-4 py-3 align-top sm:px-5 sm:py-4">
                        {isUnassigned ? (
                          <select
                            value={order.vendorId}
                            onChange={(e) => updateOrder(order.id, { vendorId: e.target.value })}
                            className="w-full rounded-lg px-2 py-2 text-xs font-semibold outline-none focus:ring-2"
                            style={{
                              ...selectStyle,
                              borderColor: order.vendorId ? P.blue : P.grid,
                            }}
                          >
                            <option value="">Select vendor…</option>
                            {activeVendors.map((v) => (
                              <option key={v.vendorId} value={v.vendorId}>
                                {v.companyName} ({v.vendorId})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="break-words text-xs font-bold" style={{ color: P.text }}>
                            {vendorName || order.vendorId || '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top sm:px-5 sm:py-4">
                        {isUnassigned ? (
                          <select
                            value={order.driverId}
                            onChange={(e) => handleDriverSelect(order.id, e.target.value)}
                            className="w-full rounded-lg px-2 py-2 text-xs font-semibold outline-none focus:ring-2"
                            style={{
                              ...selectStyle,
                              borderColor: order.driverId ? P.blue : P.grid,
                            }}
                          >
                            <option value="">Select driver…</option>
                            {driverRoster.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name} ({d.id})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="break-words text-xs font-bold" style={{ color: P.text }}>
                            {driverName || order.driverId || '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-center sm:px-5 sm:py-4">
                        {isUnassigned ? (
                          <div className="flex flex-col items-stretch gap-2 sm:items-center">
                            <button
                              type="button"
                              disabled={analyzingOrderId === order.id}
                              onClick={() => handleSmartSuggest(order.id)}
                              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[10px] font-black uppercase tracking-wide shadow-sm transition disabled:cursor-wait disabled:opacity-70"
                              style={
                                analyzingOrderId === order.id ? btnLightBlueDisabled : btnLightBlue
                              }
                              onMouseEnter={(e) =>
                                applyBtnHover(e, analyzingOrderId !== order.id)
                              }
                              onMouseLeave={(e) =>
                                applyBtnLeave(e, analyzingOrderId !== order.id)
                              }
                            >
                              <Sparkles className="h-3.5 w-3.5 shrink-0" />
                              {analyzingOrderId === order.id ? 'Analyzing…' : '✨ Smart Suggest'}
                            </button>
                            <button
                              type="button"
                              disabled={!canDispatch}
                              onClick={() => handleDispatch(order.id)}
                              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[10px] font-black uppercase tracking-wide shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
                              style={canDispatch ? btnLightBlue : btnLightBlueDisabled}
                              onMouseEnter={(e) => applyBtnHover(e, canDispatch)}
                              onMouseLeave={(e) => applyBtnLeave(e, canDispatch)}
                            >
                              <Truck className="h-3.5 w-3.5 shrink-0" />
                              ⚡ Dispatch
                            </button>
                          </div>
                        ) : (
                          <span className="text-[10px] font-bold uppercase" style={{ color: P.muted }}>
                            {order.status === 'Delivered' ? 'Completed' : 'In Pipeline'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {scorecardDriver && (
          <DriverScorecard
            driverId={scorecardDriver.id}
            driverName={scorecardDriver.name}
            tripsCompleted={scorecardDriver.tripsCompleted}
          />
        )}
      </div>

      <DispatchToast visible={toastVisible} onDismiss={dismissToast} />
    </div>
  );
}
